import { fmt } from "../../format";
import type { Column, ColumnType } from "./DataTableTypes";

const DATE_TYPES: readonly ColumnType[] = ["date", "datetime"];

/** True for the column types shown as a date and time. */
export function isDateColumn<R>(col: Column<R>): boolean {
  return col.type !== undefined && DATE_TYPES.includes(col.type);
}

/** Raw value for sorting and filtering: the column's accessor, or row[key]. */
export function rawValue<R>(row: R, col: Column<R>): unknown {
  if (col.value) return col.value(row);
  return (row as Record<string, unknown>)[col.key];
}

function formatValue<R>(value: unknown, col: Column<R>): string {
  if (col.type === "money" && typeof value === "number") return fmt.money(value);
  if (col.type === "number" && typeof value === "number") return fmt.number(value);
  if (isDateColumn(col) && (typeof value === "string" || typeof value === "number")) return fmt.dateTime(value);
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

/** Plain text of a cell, used for search, filters, CSV export and the hover title. */
export function cellText<R>(row: R, col: Column<R>): string {
  if (col.text) return col.text(row);
  if (col.type === "badge" && col.badge) return col.badge(row)?.label ?? "";
  const value = rawValue(row, col);
  if (value === null || value === undefined) return "";
  return formatValue(value, col);
}

/** Value handed to RelativeTime: ISO strings pass through, timestamps become ISO strings. */
export function dateCellValue<R>(row: R, col: Column<R>): string | null {
  const value = rawValue(row, col);
  if (typeof value === "string") return value;
  return value ? new Date(value as number).toISOString() : null;
}
